import { ChangeEvent } from 'react';
import { useTranslations } from 'next-intl';
import classNames from 'classnames';
import { IconsMap } from '@nx-frontend/shared/ui/icon';
import { useToast } from '@nx-frontend/shared/ui/toast';
import { getBgClassNames, getBorderClassNames, getTextClassNames } from '@nx-frontend/shared/themes';
import {
  PortalBgType,
  PortalBorderType,
  PortalClassState,
  PortalStyleConfig,
  PortalTextType,
  ThemeColorSchemas,
} from '@nx-frontend/shared/types';

interface UploadAreaProps {
  themeColors: ThemeColorSchemas;
  themeStyle: PortalStyleConfig;
  extendedView?: boolean;
  disabled?: boolean;
  onFilesSelect: (files: FileList | null) => void;
  multiple?: boolean;
  accept?: string;
  borderThin?: boolean;
}

export const UploadArea: React.FC<UploadAreaProps> = ({
  themeColors,
  themeStyle,
  extendedView,
  disabled,
  onFilesSelect,
  multiple = true,
  accept,
  borderThin,
}) => {
  const t = useTranslations('shared');
  const { toast } = useToast();
  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const files = (event.target as HTMLInputElement).files;
    onFilesSelect(files);
  };
  const handleClick = () => {
    if (disabled) {
      toast({ title: 'You do not have permission to upload files. Please contact Portal owner for assistance' });
    }
  };

  const borderClass = borderThin ? 'border' : 'border-2';
  return (
    <div
      onClick={handleClick}
      className={classNames(
        'w-full transition-all border-dashed rounded-xl overflow-hidden relative flex flex-col items-center justify-center p-4 gap-3',
        {
          'h-[127px]': extendedView,
          'h-[40px]': !extendedView,
        },
        disabled
          ? [
              'cursor-not-allowed',
              getBgClassNames(themeColors, PortalBgType.SURFACE_DISABLE),
              getBorderClassNames(
                themeColors,
                { ...themeStyle, border: { className: [borderClass] } },
                PortalBorderType.SURFACE_DISABLE
              ),
            ]
          : [
              getBgClassNames(themeColors, PortalBgType.SURFACE_VARIANT),
              getBgClassNames(themeColors, PortalBgType.SURFACE_VARIANT_HOVER, [PortalClassState.HOVER]),
              getBorderClassNames(
                themeColors,
                { ...themeStyle, border: { className: [borderClass] } },
                PortalBorderType.PRIMARY
              ),
            ]
      )}
    >
      {extendedView && (
        <>
          <div
            className={classNames(
              'w-9 h-9 rounded-full flex items-center justify-center',
              getBgClassNames(themeColors, PortalBgType.SURFACE_DISABLE)
            )}
          >
            <span
              className={classNames(
                disabled ? 'text-gray-400' : [getTextClassNames(themeColors, PortalTextType.PRIMARY)]
              )}
            >
              {IconsMap.UPLOAD}
            </span>
          </div>
          <p className={classNames('text-center text-base', getTextClassNames(themeColors, PortalTextType.SURFACE))}>
            {t('upload-area.upload_files1')}{' '}
            <span
              className={classNames(
                disabled ? 'text-gray-400' : [getTextClassNames(themeColors, PortalTextType.PRIMARY)]
              )}
            >
              <strong>{t('upload-area.upload_files2')}</strong>
            </span>
          </p>
        </>
      )}
      {!extendedView && (
        <div
          className={classNames(
            'flex items-center justify-center text-base gap-2.5',
            disabled ? 'text-gray-400' : [getTextClassNames(themeColors, PortalTextType.PRIMARY)]
          )}
        >
          <span>{IconsMap.PLUS}</span>
          <span>Add more files</span>
        </div>
      )}
      {!disabled && (
        <input
          style={{ paddingLeft: '100%' }}
          type="file"
          multiple={multiple}
          accept={accept}
          className={classNames(
            'w-full h-full absolute top-0 left-0 opacity-0',
            disabled ? 'cursor-not-allowed' : 'cursor-pointer'
          )}
          onChange={handleChange}
          disabled={disabled}
        />
      )}
    </div>
  );
};
